import { WORK_ITEMS, type WorkItem } from "@/lib/content/work";
import { workSerialCategoryLetter } from "@/lib/content/work-categories";

/** Two-character portfolio code: sequence digit/letter + category letter (e.g. `3M`, `0D`). */
export type WorkProjectCode = `${string}${string}`;

const SEQUENCE = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export {
  WORK_CATEGORY_CODE_LETTER,
  WORK_SERIAL_CATEGORY_CODE_LETTER,
} from "@/lib/content/work-categories";

function primaryCategoryLetter(item: WorkItem): string {
  return workSerialCategoryLetter(item);
}

function buildWorkProjectCodeMap(): Record<string, WorkProjectCode> {
  const counters: Record<string, number> = {};
  const codes: Record<string, WorkProjectCode> = {};

  for (const item of WORK_ITEMS) {
    const letter = primaryCategoryLetter(item);
    const index = counters[letter] ?? 0;

    if (index >= SEQUENCE.length) {
      throw new Error(
        `Too many work items for category letter ${letter}; expand SEQUENCE.`,
      );
    }

    counters[letter] = index + 1;
    codes[item.id] = `${SEQUENCE[index]}${letter}` as WorkProjectCode;
  }

  return codes;
}

export const WORK_PROJECT_CODE_BY_ID: Readonly<
  Record<string, WorkProjectCode>
> = buildWorkProjectCodeMap();

const WORK_ID_BY_PROJECT_CODE: Readonly<Partial<Record<WorkProjectCode, string>>> =
  Object.fromEntries(
    Object.entries(WORK_PROJECT_CODE_BY_ID).map(([workId, code]) => [
      code,
      workId,
    ]),
  );

export function normalizeWorkProjectCode(
  value: string,
): WorkProjectCode | null {
  const code = value.trim().toUpperCase();
  if (!workProjectCodePattern(code as WorkProjectCode)) {
    return null;
  }
  return code as WorkProjectCode;
}

export function getWorkIdByProjectCode(
  code: WorkProjectCode,
): string | undefined {
  return WORK_ID_BY_PROJECT_CODE[code];
}

export function isWorkProjectCode(value: string): boolean {
  const code = normalizeWorkProjectCode(value);
  return code != null && WORK_ID_BY_PROJECT_CODE[code] != null;
}

export function getWorkProjectCode(workId: string): WorkProjectCode {
  const code = WORK_PROJECT_CODE_BY_ID[workId];
  if (!code) {
    throw new Error(`Missing project code for work id "${workId}".`);
  }
  return code;
}

export function resolveWorkProjectCode(item: WorkItem): WorkProjectCode {
  return getWorkProjectCode(item.id);
}

export function workProjectCodePattern(code: WorkProjectCode): boolean {
  return /^[0-9A-Z]{2}$/.test(code);
}

/** Dev/CI guard — every WORK_ITEMS entry must have a unique two-character code. */
export function assertWorkProjectCodesComplete(): void {
  const ids = new Set<string>();
  const codes = new Set<string>();

  for (const item of WORK_ITEMS) {
    if (ids.has(item.id)) {
      throw new Error(`Duplicate work id in WORK_ITEMS: ${item.id}`);
    }
    ids.add(item.id);

    const code = getWorkProjectCode(item.id);
    if (!workProjectCodePattern(code)) {
      throw new Error(`Invalid project code "${code}" for ${item.id}`);
    }
    if (codes.has(code)) {
      throw new Error(`Duplicate project code "${code}" assigned to ${item.id}`);
    }
    codes.add(code);
  }

  if (Object.keys(WORK_PROJECT_CODE_BY_ID).length !== WORK_ITEMS.length) {
    throw new Error("WORK_PROJECT_CODE_BY_ID is out of sync with WORK_ITEMS.");
  }
}
